import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, List, message, Row } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import Colors from '../../constants/Colors';
import DisplayText from '../../common/text/DisplayText';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import CustomTableListItem from '../Forms/CustomTableListItem';
import CompanySettingsCard from '../CompanySettingsCard';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import WarningHeader from '../../common/text/WarningHeader';
import {
  getProjectBillingRateTemplates,
  createProjectBillingRateTemplate,
  updateProjectBillingRateTemplate,
  deleteProjectBillingRateTemplate,
} from '../../projectBillingRates/states/projectBillingRates.actions';
import ProjectBillingTemplateAddEditDrawer from './ProjectBillingTemplateAddEditDrawer';

const deleteModal = (onOk) => CustomConfirmModal({
  title: (
    <WarningHeader
      justify="center"
      title="Are you sure you want to delete this project billing rate template?"
    />
  ),
  onOk,
});

export default function ProjectBillingRateTemplate() {
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState();

  const dispatch = useDispatch();
  const projectBillingRateTemplates = useSelector((state) => state.projectBillingRates.templates);

  useEffect(() => {
    dispatch(getProjectBillingRateTemplates());
  }, []);

  const projectBillingRateTemplatesArray = useMemo(
    () => Object.values(projectBillingRateTemplates ?? {}),
    [projectBillingRateTemplates],
  );

  const onAddClicked = useCallback(() => {
    setSelectedEntry();
    setShowDrawer(true);
  }, []);

  const clearValues = useCallback(() => {
    setSelectedEntry();
    setShowDrawer(false);
  }, []);

  const onSubmit = useCallback(async (values) => {
    try {
      const payload = {
        name: values.name?.trim(),
        type: values.type,
        regularBillingRate: values.regularBillingRate ?? null,
        otBillingRate: values.otBillingRate ?? null,
        dtBillingRate: values.dtBillingRate ?? null,
        regularCostRate: values.regularCostRate ?? null,
        otCostRate: values.otCostRate ?? null,
        dtCostRate: values.dtCostRate ?? null,
        hourlyBillingRate: values.hourlyBillingRate ?? null,
        dailyBillingRate: values.dailyBillingRate ?? null,
        weeklyBillingRate: values.weeklyBillingRate ?? null,
        monthlyBillingRate: values.monthlyBillingRate ?? null,
        hourlyCostRate: values.hourlyCostRate ?? null,
        dailyCostRate: values.dailyCostRate ?? null,
        weeklyCostRate: values.weeklyCostRate ?? null,
        monthlyCostRate: values.monthlyCostRate ?? null,
        userIds: values.userIds ?? [],
        positionIds: values.positionIds ?? [],
        equipmentTypeIds: values.equipmentTypeIds ?? [],
        equipmentIds: values.equipmentIds ?? [],
      };
      const result = selectedEntry
        ? await dispatch(updateProjectBillingRateTemplate(selectedEntry.id, payload))
        : await dispatch(createProjectBillingRateTemplate(payload));
      if (result?.success) {
        message.success('Project billing rate template saved successfully.');
        clearValues();
      } else {
        message.error(result?.message || 'Could not save project billing rate template.');
      }
    } catch (err) {
      message.error(err.message);
    }
  }, [clearValues, selectedEntry]);

  const onDelete = useCallback(
    (id) => deleteModal(() => dispatch(deleteProjectBillingRateTemplate(id))),
    [],
  );

  const onEdit = useCallback((id) => {
    const entry = projectBillingRateTemplates[id];
    setSelectedEntry(entry);
    setShowDrawer(true);
  }, [projectBillingRateTemplates]);

  const renderItem = useCallback((item) => (
    <CustomTableListItem
      onDelete={onDelete}
      onEdit={onEdit}
      item={{
        id: item.id,
        name: item.name,
        description: item.type === 'labour' ? 'Labour' : 'Equipment',
      }}
    />
  ), [onDelete, onEdit]);

  return (
    <>
      <CompanySettingsCard title="Project Billing Rate Templates">
        <Row style={{ margin: 10 }} align="bottom">
          <Col span={18}>
            <DisplayText
              title="Templates"
              style={{ fontFamily: 'roboto-medium', marginBottom: 0 }}
            />
            <DisplayText
              title={(
                <div style={{ width: '100%' }}>
                  Configure reusable billing rate templates for projects
                </div>
              )}
              style={{
                fontFamily: 'roboto-regular',
                marginBottom: 0,
                color: Colors.ONTRACCR_OPACITY_GRAY,
              }}
            />
          </Col>
          <Col span={6}>
            <OnTraccrButton
              title="Add"
              icon={<PlusOutlined />}
              onClick={onAddClicked}
            />
          </Col>
        </Row>
        <List
          style={{ marginLeft: 15 }}
          dataSource={projectBillingRateTemplatesArray}
          renderItem={renderItem}
        />
      </CompanySettingsCard>
      <ProjectBillingTemplateAddEditDrawer
        selectedEntry={selectedEntry}
        visible={showDrawer}
        toggle={clearValues}
        onSubmit={onSubmit}
        key={selectedEntry?.id ?? 'new'}
      />
    </>
  );
}
